import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Plus, Users } from 'lucide-react';
import {
  useCreateOrganisation,
  useOrganisations,
} from '../../lib/api/queries';

/**
 * `/organisations` — DPAS-staff surface for managing the client
 * organisations we audit. List, filter, and create; the per-org detail
 * page (rename, client users, audits) lives at `/organisations/:id`.
 */
export function OrganisationsListPage() {
  const { data, isLoading, error } = useOrganisations();
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Client organisations</h1>
          <p className="text-sm text-gray-500 mt-1">
            The customers you audit. Each organisation can own multiple
            audits and DPIAs and can have its own read-only client logins.
          </p>
        </div>
        <button type="button"
                onClick={() => setCreating(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium
                           text-white bg-brand-600 hover:bg-brand-700 rounded-md
                           transition-colors">
          <Plus className="w-4 h-4" aria-hidden /> New organisation
        </button>
      </header>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {error && (
        <p className="text-sm text-danger-500">
          We couldn't load the organisations list. Try refreshing.
        </p>
      )}
      {data && data.length === 0 && !isLoading && (
        <div className="rounded-md border border-gray-200 bg-white p-8 text-center">
          <Building2 className="w-8 h-8 mx-auto text-gray-400 mb-2" aria-hidden />
          <p className="text-sm font-medium text-gray-700">No organisations yet.</p>
          <p className="text-xs text-gray-500 mt-1">
            Create your first client organisation to start audits and (once
            you're ready) publish reports to their people.
          </p>
        </div>
      )}

      {data && data.length > 0 && (
        <ul className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
          {data.map(o => (
            <li key={o.id}>
              <Link to={`/organisations/${o.id}`}
                    className="block p-4 hover:bg-gray-50 transition-colors
                               flex items-center gap-4">
                <div className="w-10 h-10 rounded-md bg-brand-50 text-brand-600
                                flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5" aria-hidden />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{o.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Added {formatDate(o.createdAtUtc)}
                  </p>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500 shrink-0">
                  <span className="inline-flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" aria-hidden />
                    {o.userCount} user{o.userCount === 1 ? '' : 's'}
                  </span>
                  <span>{o.auditCount} audit{o.auditCount === 1 ? '' : 's'}</span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {creating && (
        <CreateOrganisationModal
          onClose={() => setCreating(false)}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------

function CreateOrganisationModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const create = useCreateOrganisation();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (name.trim().length === 0) {
      setErr('A name is required.');
      return;
    }
    try {
      await create.mutateAsync({ name: name.trim() });
      onClose();
    } catch (e) {
      const status = (e as { status?: number })?.status;
      if (status === 409) {
        setErr('An organisation with that name already exists in your tenant.');
      } else {
        setErr('Failed to create organisation. Try again.');
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4"
         onClick={onClose}>
      <form onSubmit={submit}
            onClick={e => e.stopPropagation()}
            className="bg-white w-full max-w-md rounded-lg shadow-xl overflow-hidden">
        <header className="px-5 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-base font-semibold text-gray-900">New organisation</h2>
        </header>
        <div className="px-5 py-4 space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Name</span>
            <input type="text"
                   autoFocus
                   value={name}
                   onChange={e => setName(e.target.value)}
                   placeholder="e.g. Voluptatem deserunt"
                   className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                              focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </label>
          {err && <p className="text-sm text-danger-500">{err}</p>}
        </div>
        <footer className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
          <button type="button"
                  onClick={onClose}
                  disabled={create.isPending}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white
                             border border-gray-300 rounded-md hover:bg-gray-50
                             transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button type="submit"
                  disabled={create.isPending}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-brand-600
                             hover:bg-brand-700 rounded-md transition-colors
                             disabled:opacity-50">
            {create.isPending ? 'Creating…' : 'Create organisation'}
          </button>
        </footer>
      </form>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}
